/**
 * FastAPIPlugin — Framework plugin for FastAPI (Python).
 *
 * Extracts:
 * - Route decorators: @app.get('/path'), @router.post('/path'), etc.
 * - Depends() dependency injection in function parameters
 * - Response model annotations on route decorators
 * - app.include_router() mounts
 *
 * Uses tree-sitter-python for AST parsing.
 */
import fs from 'node:fs';
import path from 'node:path';
import { err, ok } from 'neverthrow';
import type { TraceMcpResult } from '../../../../../errors.js';
import { parseError } from '../../../../../errors.js';
import { getParser, type TSNode } from '../../../../../parser/tree-sitter.js';
import type {
  EdgeTypeDeclaration,
  FileParseResult,
  FrameworkPlugin,
  PluginManifest,
  ProjectContext,
  RawEdge,
  RawRoute,
  ResolveContext,
} from '../../../../../plugin-api/types.js';
import { escapeRegExp } from '../../../../../utils/security.js';
import { findEnclosingSymbol, lineOfIndex } from '../../_shared/regex-edges.js';

/**
 * Matches a FastAPI dependency: `Depends(get_session)` / `Depends(get_db)`,
 * including the `Annotated[T, Depends(dep)]` form. Captures the dependency
 * callable's identifier. Calls like `Depends(factory())` or lambdas are skipped.
 */
const DEPENDS_RE = /\bDepends\(\s*([A-Za-z_]\w*)\s*\)/g;

/** HTTP methods recognized on FastAPI route decorators. */
const HTTP_METHODS = new Set(['get', 'post', 'put', 'delete', 'patch', 'options', 'head']);

/**
 * Check if a Python project has a given package in its dependencies.
 * Reads pyproject.toml parsed deps and requirements.txt from the ProjectContext,
 * falling back to reading files from disk.
 */
function hasPythonDep(ctx: ProjectContext, pkg: string): boolean {
  const lowerPkg = pkg.toLowerCase();

  // Check parsed pyproject.toml deps
  if (ctx.pyprojectToml) {
    const deps = ctx.pyprojectToml._parsedDeps as string[] | undefined;
    if (deps?.includes(lowerPkg)) return true;
  }

  // Check parsed requirements.txt
  if (ctx.requirementsTxt?.includes(lowerPkg)) return true;

  // Fallback: read from disk
  try {
    const pyprojectPath = path.join(ctx.rootPath, 'pyproject.toml');
    const content = fs.readFileSync(pyprojectPath, 'utf-8');
    const re = new RegExp(`["']${escapeRegExp(pkg)}[>=<\\[!~\\s"']`, 'i');
    if (re.test(content)) return true;
  } catch {
    /* not found */
  }

  try {
    const reqPath = path.join(ctx.rootPath, 'requirements.txt');
    const content = fs.readFileSync(reqPath, 'utf-8');
    const re = new RegExp(`^${escapeRegExp(pkg)}\\b`, 'im');
    if (re.test(content)) return true;
  } catch {
    /* not found */
  }

  return false;
}

export class FastAPIPlugin implements FrameworkPlugin {
  manifest: PluginManifest = {
    name: 'fastapi',
    version: '1.0.0',
    priority: 10,
    category: 'framework',
    dependencies: [],
  };

  detect(ctx: ProjectContext): boolean {
    return hasPythonDep(ctx, 'fastapi');
  }

  registerSchema() {
    return {
      edgeTypes: [
        {
          name: 'fastapi_route',
          category: 'fastapi',
          description: 'FastAPI route decorator → handler function',
        },
        {
          name: 'fastapi_depends',
          category: 'fastapi',
          description: 'FastAPI Depends() dependency injection',
        },
        {
          name: 'fastapi_request_model',
          category: 'fastapi',
          description: 'Route handler → Pydantic request model',
        },
        {
          name: 'fastapi_response_model',
          category: 'fastapi',
          description: 'Route decorator → Pydantic response model',
        },
        {
          name: 'fastapi_router_mounts',
          category: 'fastapi',
          description: 'app.include_router() mount',
        },
      ] satisfies EdgeTypeDeclaration[],
    };
  }

  async extractNodes(
    filePath: string,
    content: Buffer,
    language: string,
  ): Promise<TraceMcpResult<FileParseResult>> {
    if (language !== 'python') {
      return ok({ status: 'ok', symbols: [] });
    }

    const source = content.toString('utf-8');
    // Quick check — skip files that don't mention fastapi-related patterns
    if (
      !source.includes('fastapi') &&
      !source.includes('FastAPI') &&
      !source.includes('APIRouter') &&
      !source.includes('@app.') &&
      !source.includes('@router.')
    ) {
      return ok({ status: 'ok', symbols: [] });
    }

    const result: FileParseResult = {
      status: 'ok',
      symbols: [],
      routes: [],
      edges: [],
      warnings: [],
    };

    try {
      const parser = await getParser('python');
      const tree = parser.parse(source);
      try {
        const root = tree.rootNode;

        const routerPrefixes = this.buildRouterPrefixMap(root);
        this.extractRoutes(root, source, filePath, result, routerPrefixes);
        this.extractRouterMounts(root, source, filePath, result);
      } finally {
        tree.delete();
      }
    } catch (e: unknown) {
      return err(
        parseError(filePath, `FastAPI parse error: ${e instanceof Error ? e.message : String(e)}`),
      );
    }

    if (result.routes!.length > 0 || result.edges!.length > 0) {
      result.frameworkRole = 'fastapi_routes';
    }

    return ok(result);
  }

  /**
   * Pass 2: resolve `Depends(dep)` dependency injection to symbol-level edges.
   *
   * The pass-1 `fastapi_depends` edge is metadata-only (no resolvable target),
   * so a dependency function like `get_session` had zero graph dependents. Here
   * we have cross-file symbol access: for every `Depends(dep)` site we attach a
   * `fastapi_depends` edge from the enclosing handler to the resolved dependency
   * function/class, so it surfaces in get_change_impact, find_usages, and the
   * call graph. Regex-based (sync) — also catches the `Annotated[..., Depends]`
   * form the AST pass-1 extractor misses.
   */
  resolveEdges(ctx: ResolveContext): TraceMcpResult<RawEdge[]> {
    const edges: RawEdge[] = [];

    const pyFiles = ctx.getAllFiles().filter((f) => f.language === 'python');
    if (pyFiles.length === 0) return ok(edges);

    // TRA-2505 / TRA-2434: Scope-aware: fast-path when resolving incrementally.
    const scope = ctx.changeScope;
    const targetFiles =
      scope && scope.changedFileIds && scope.changedFileIds.size > 0
        ? pyFiles.filter((f) => scope.changedFileIds.has(f.id))
        : pyFiles;

    if (targetFiles.length === 0) return ok(edges);

    // Index top-level functions/classes by name for dependency resolution.
    type Sym = { id: number; symbolId: string; name: string; kind: string };
    const byName = new Map<string, Sym[]>();
    const symsByFile = new Map<number, ReturnType<ResolveContext['getSymbolsByFile']>>();
    for (const f of pyFiles) {
      const syms = ctx.getSymbolsByFile(f.id);
      symsByFile.set(f.id, syms);
      for (const s of syms) {
        // Top-level only: `file::name#kind` has exactly one `::` separator.
        if (s.kind !== 'function' && s.kind !== 'class') continue;
        if (s.symbolId.split('::').length !== 2) continue;
        const list = byName.get(s.name) ?? [];
        list.push({ id: s.id, symbolId: s.symbolId, name: s.name, kind: s.kind });
        byName.set(s.name, list);
      }
    }

    for (const f of targetFiles) {
      const source = ctx.readFile(f.path);
      if (!source || (!source.includes('Depends') && !source.includes('depends'))) continue;
      const fileSyms = symsByFile.get(f.id) ?? [];

      DEPENDS_RE.lastIndex = 0;
      let m: RegExpExecArray | null;
      // biome-ignore lint/suspicious/noAssignInExpressions: standard regex exec loop
      while ((m = DEPENDS_RE.exec(source)) !== null) {
        const depName = m[1];
        const line = lineOfIndex(source, m.index);
        const handler = findEnclosingSymbol(fileSyms, line);
        if (!handler) continue;

        // Resolve the dependency: same-file top-level symbol wins, else an
        // unambiguous single match across the project (mirrors python-types).
        const candidates = byName.get(depName);
        if (!candidates || candidates.length === 0) continue;
        const sameFile = candidates.find((c) =>
          fileSyms.some((s) => s.id === c.id && s.symbolId === c.symbolId),
        );
        let target: Sym | undefined = sameFile;
        if (!target) {
          if (candidates.length === 1) target = candidates[0];
          else continue; // ambiguous — do not guess
        }
        if (target.id === handler.id) continue;

        edges.push({
          edgeType: 'fastapi_depends',
          sourceNodeType: 'symbol',
          sourceRefId: handler.id,
          targetSymbolId: target.symbolId,
          metadata: { dependency: depName, handler: handler.name, file: f.path, line },
          resolution: 'ast_resolved',
        });
      }
    }

    return ok(edges);
  }

  /**
   * Extract route decorators from decorated_definition nodes.
   *
   * Patterns:
   *   @app.get("/path")
   *   @app.post("/path", response_model=Foo)
   *   @router.put("/path")
   */
  private extractRoutes(
    root: TSNode,
    source: string,
    filePath: string,
    result: FileParseResult,
    routerPrefixes: Map<string, string> = new Map(),
  ): void {
    const decoratedDefs = this.findAllByType(root, 'decorated_definition');

    for (const decoratedDef of decoratedDefs) {
      // The function_definition is a child of the decorated_definition
      const funcDef = decoratedDef.children.find((c: TSNode) => c.type === 'function_definition');
      if (!funcDef) continue;

      const funcName = funcDef.childForFieldName('name')?.text ?? 'unknown';
      const parameters = funcDef.childForFieldName('parameters');

      // Iterate decorators
      for (const child of decoratedDef.children) {
        if (child.type !== 'decorator') continue;

        const decoratorExpr = child.children.find(
          (c: TSNode) => c.type === 'call' || c.type === 'attribute',
        );
        if (!decoratorExpr) continue;

        // Resolve the call expression: @app.get("/path") or @router.post("/path", ...)
        let callNode: TSNode | null = null;
        if (decoratorExpr.type === 'call') {
          callNode = decoratorExpr;
        }
        if (!callNode) continue;

        const funcRef = callNode.childForFieldName('function');
        if (!funcRef || funcRef.type !== 'attribute') continue;

        const methodName = funcRef.childForFieldName('attribute')?.text;
        if (!methodName || !HTTP_METHODS.has(methodName)) continue;

        // Extract URI from the first positional argument
        const args = callNode.childForFieldName('arguments');
        if (!args) continue;

        const rawUri = this.extractFirstStringArg(args);
        if (!rawUri) continue;

        // Compose the APIRouter(prefix=...) of the decorator object. For
        // `router = APIRouter(prefix="/users")` + `@router.post("/")`, FastAPI
        // serves `/users`; without this the route was stored as bare `/` and
        // get_request_flow("/users") missed it entirely.
        const objectName = funcRef.childForFieldName('object')?.text;
        const prefix = objectName ? routerPrefixes.get(objectName) : undefined;
        const uri = prefix ? this.joinRoutePath(prefix, rawUri) : rawUri;

        // Extract response_model keyword argument
        const responseModel = this.extractKeywordArg(args, 'response_model');

        const route: RawRoute = {
          method: methodName.toUpperCase(),
          uri,
          controllerSymbolId: funcName,
          line: funcDef.startPosition.row + 1,
          // `router` is the decorator object var (`@router.get` → "router",
          // `@app.get` → "app"); `baseUri` is the within-file-composed path.
          // Pass 2 (resolveFastapiRouterMounts) reads these to prepend a
          // cross-file `app.include_router(router, prefix=...)` mount prefix,
          // always recomputing from baseUri so re-indexing is idempotent.
          metadata: { router: objectName ?? '', baseUri: uri },
        };
        result.routes!.push(route);

        // Emit fastapi_route edge
        result.edges!.push({
          edgeType: 'fastapi_route',
          metadata: {
            method: route.method,
            uri: route.uri,
            handler: funcName,
            filePath,
            line: route.line,
          },
        });

        // Emit fastapi_response_model edge if present
        if (responseModel) {
          result.edges!.push({
            edgeType: 'fastapi_response_model',
            metadata: {
              handler: funcName,
              responseModel,
              filePath,
            },
          });
        }

        // Extract Depends() from function parameters
        if (parameters) {
          this.extractDepends(parameters, funcName, filePath, result);
        }

        // Extract Pydantic request model from type-annotated parameters
        if (parameters) {
          this.extractRequestModels(parameters, funcName, filePath, result);
        }
      }
    }
  }

  /**
   * Extract Depends(some_func) from function parameters.
   *
   * Pattern: param: Type = Depends(get_db)
   * In the AST: default_parameter → default value is call with function named "Depends"
   */
  private extractDepends(
    parameters: TSNode,
    funcName: string,
    filePath: string,
    result: FileParseResult,
  ): void {
    for (const param of parameters.children) {
      if (param.type !== 'default_parameter' && param.type !== 'typed_default_parameter') continue;

      // Find the default value (the last significant child)
      const defaultValue =
        param.childForFieldName('value') ?? param.children[param.children.length - 1];
      if (!defaultValue || defaultValue.type !== 'call') continue;

      const callFunc = defaultValue.childForFieldName('function');
      if (!callFunc) continue;

      // Check for Depends() or Depends
      const callFuncName = callFunc.text;
      if (callFuncName !== 'Depends') continue;

      // Extract the dependency function name from the first argument
      const callArgs = defaultValue.childForFieldName('arguments');
      if (!callArgs) continue;

      const depTarget = this.getFirstArgText(callArgs);
      if (!depTarget) continue;

      result.edges!.push({
        edgeType: 'fastapi_depends',
        metadata: {
          handler: funcName,
          dependency: depTarget,
          filePath,
        },
      });
    }
  }

  /**
   * Extract Pydantic request models from type annotations in parameters.
   *
   * Pattern: async def create_item(item: ItemCreate):
   * We look for typed_parameter nodes where the annotation is a capitalized identifier
   * (likely a Pydantic model). We skip basic types like str, int, float, bool, etc.
   */
  private extractRequestModels(
    parameters: TSNode,
    funcName: string,
    filePath: string,
    result: FileParseResult,
  ): void {
    const builtinTypes = new Set([
      'str',
      'int',
      'float',
      'bool',
      'bytes',
      'list',
      'dict',
      'tuple',
      'set',
      'frozenset',
      'None',
      'Any',
      'Optional',
      'List',
      'Dict',
      'Request',
      'Response',
      'HTTPException',
    ]);

    for (const param of parameters.children) {
      if (param.type !== 'typed_parameter' && param.type !== 'typed_default_parameter') continue;

      const annotation = param.childForFieldName('type');
      if (!annotation) continue;

      const typeName = annotation.text;
      // Skip builtin types and lowercase names (path params, query params)
      if (!typeName || builtinTypes.has(typeName) || /^[a-z]/.test(typeName)) continue;
      // Skip generic types like Optional[X], List[X]
      if (typeName.includes('[')) continue;

      result.edges!.push({
        edgeType: 'fastapi_request_model',
        metadata: {
          handler: funcName,
          requestModel: typeName,
          filePath,
        },
      });
    }
  }

  /**
   * Extract app.include_router(router, prefix='/api/v1') calls.
   */
  private extractRouterMounts(
    root: TSNode,
    source: string,
    filePath: string,
    result: FileParseResult,
  ): void {
    const calls = this.findAllByType(root, 'call');

    for (const call of calls) {
      const funcRef = call.childForFieldName('function');
      if (!funcRef || funcRef.type !== 'attribute') continue;

      const attr = funcRef.childForFieldName('attribute')?.text;
      if (attr !== 'include_router') continue;

      const args = call.childForFieldName('arguments');
      if (!args) continue;

      const routerName = this.getFirstArgText(args);
      const prefix = this.extractKeywordArg(args, 'prefix');
      const tags = this.extractKeywordArg(args, 'tags');

      if (routerName) {
        result.edges!.push({
          edgeType: 'fastapi_router_mounts',
          metadata: {
            router: routerName,
            prefix: prefix ?? '',
            tags: tags ?? '',
            filePath,
            line: call.startPosition.row + 1,
          },
        });
      }
    }
  }

  /**
   * Map router variable name → its APIRouter(prefix="...") so route decorators
   * on that variable can be resolved to their full served path. Scans
   * `name = APIRouter(prefix="/x")` assignments in the file.
   */
  private buildRouterPrefixMap(root: TSNode): Map<string, string> {
    const map = new Map<string, string>();
    for (const assign of this.findAllByType(root, 'assignment')) {
      const left = assign.childForFieldName('left');
      const right = assign.childForFieldName('right');
      if (!left || left.type !== 'identifier' || !right || right.type !== 'call') continue;

      const fn = right.childForFieldName('function');
      const fnText = fn?.text ?? '';
      // Match `APIRouter(...)` or `fastapi.APIRouter(...)`.
      if (fnText !== 'APIRouter' && !fnText.endsWith('.APIRouter')) continue;

      const args = right.childForFieldName('arguments');
      const prefix = args ? this.extractKeywordArg(args, 'prefix') : null;
      if (prefix) map.set(left.text, prefix);
    }
    return map;
  }

  /**
   * Join a router prefix and a route path the way FastAPI does, then normalize:
   * collapse duplicate slashes and drop a lone trailing slash so `/users` and
   * `/users/{id}` are produced (not `/users/` or `/users//{id}`).
   */
  private joinRoutePath(prefix: string, path: string): string {
    let joined = `${prefix}${path}`.replace(/\/{2,}/g, '/');
    if (joined.length > 1 && joined.endsWith('/')) joined = joined.slice(0, -1);
    return joined || '/';
  }

  // ─── Tree-sitter helpers ───────────────────────────────────────────

  /** Recursively find all nodes of a given type. */
  private findAllByType(node: TSNode, type: string): TSNode[] {
    const results: TSNode[] = [];
    if (node.type === type) results.push(node);
    for (const child of node.children ?? []) {
      results.push(...this.findAllByType(child, type));
    }
    return results;
  }

  /** Get the text of the first positional string argument in an argument_list. */
  private extractFirstStringArg(args: TSNode): string | null {
    for (const child of args.children ?? []) {
      if (child.type === 'string') {
        return this.unquote(child.text);
      }
      // Handle concatenated_string
      if (child.type === 'concatenated_string') {
        return this.unquote(child.children[0]?.text ?? '');
      }
    }
    return null;
  }

  /** Get the text of the first positional (non-keyword) argument. */
  private getFirstArgText(args: TSNode): string | null {
    for (const child of args.children ?? []) {
      if (
        child.type === 'keyword_argument' ||
        child.type === '(' ||
        child.type === ')' ||
        child.type === ','
      )
        continue;
      return child.text;
    }
    return null;
  }

  /** Extract a keyword argument value as text. */
  private extractKeywordArg(args: TSNode, name: string): string | null {
    for (const child of args.children ?? []) {
      if (child.type !== 'keyword_argument') continue;
      const key = child.childForFieldName('name')?.text;
      if (key !== name) continue;
      const value = child.childForFieldName('value');
      if (!value) continue;
      if (value.type === 'string') return this.unquote(value.text);
      return value.text;
    }
    return null;
  }

  /** Remove quotes from a Python string literal. */
  private unquote(s: string): string {
    // Handle triple-quoted, f-strings, etc.
    let text = s;
    // Strip leading f/b/r prefixes
    text = text.replace(/^[fFbBrRuU]+/, '');
    if (text.startsWith('"""') || text.startsWith("'''")) {
      return text.slice(3, -3);
    }
    if (text.startsWith('"') || text.startsWith("'")) {
      return text.slice(1, -1);
    }
    return text;
  }
}
